import { BRAND } from "../brand.js";
import PRODUCT_CATALOGUE from "../../products.config.js";
/* data, mock APIs, finance math, options, initial state.
   Ported from the prototype to a plain ES module (no globals). */

/* ---------- formatters ---------- */
export const fmt0 = (n) => "$" + Math.round(n).toLocaleString("en-AU");
export const fmt2 = (n) => "$" + Number(n).toLocaleString("en-AU", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

/* ---------- repayment math (amortisation) ---------- */
export const PERIODS = { weekly: 52, fortnightly: 26, monthly: 12 };
export function calcPayment(principal, termYears, annualRate, freq = "monthly") {
  const ppy = PERIODS[freq] || 12;
  const n = termYears * ppy;
  const r = annualRate / ppy;
  if (principal <= 0) return 0;
  if (r === 0) return principal / n;
  return (principal * (r * Math.pow(1 + r, n))) / (Math.pow(1 + r, n) - 1);
}
/* ---------- products (Step 5) ----------
   The catalogue lives in the repo-root products.config.js (shared with
   server/lib/finance.js, so the quoted rates and the serviceability model
   can never drift). Edit that file to change tiers/rates. */
export const INDICATIVE_RATE = PRODUCT_CATALOGUE.indicativeRate; // used before a product is chosen
export const PRODUCTS = PRODUCT_CATALOGUE.tiers;

/* ---------- mock AutoGrab vehicle lookup (Step 2) ----------
   NOTE: replace with the real AutoGrab call when wiring integrations. */
export const MOCK_VEHICLES = {
  ABC123: { make: "Toyota", model: "RAV4 Cruiser", year: 2021, bodyType: "SUV", colour: "Silver", vin: "JTMR**********4821", value: 38500 },
  XOZ500: { make: "Ford", model: "Ranger XLT", year: 2022, bodyType: "Ute", colour: "Blue", vin: "MPB**********0193", value: 52900 },
  ZZ12FK: { make: "Mazda", model: "CX-5 Maxx", year: 2020, bodyType: "SUV", colour: "Grey", vin: "JM0**********7762", value: 31200 },
  EVA001: { make: "Tesla", model: "Model 3", year: 2023, bodyType: "Sedan", colour: "White", vin: "5YJ**********9920", value: 49990 },
};
export function lookupVehicle(rego) {
  const key = (rego || "").toUpperCase().replace(/\s/g, "");
  return new Promise((resolve) => {
    setTimeout(() => resolve(MOCK_VEHICLES[key] || null), 950);
  });
}

/* ---------- mock licence verification (DVS / GreenID-style data capture) ---------- */
export const AU_STATES = [
  { v: "NSW", l: "New South Wales" },
  { v: "VIC", l: "Victoria" },
  { v: "QLD", l: "Queensland" },
  { v: "SA", l: "South Australia" },
  { v: "WA", l: "Western Australia" },
  { v: "TAS", l: "Tasmania" },
  { v: "ACT", l: "ACT" },
  { v: "NT", l: "Northern Territory" },
];
// Mocked verified record returned by the document-verification service.
// We capture licence details only — no card number is requested or returned.
export function verifyLicence({ number, state, firstName, lastName }) {
  return new Promise((resolve) => {
    setTimeout(() => {
      const fn = (firstName || "Jordan").trim();
      const ln = (lastName || "Avery").trim();
      resolve({
        verified: true,
        fullName: `${fn} ${ln}`,
        firstName: fn,
        lastName: ln,
        dob: "1991-03-14",
        expiry: "2029-08-31",
        state: state || "NSW",
        number: number || "",
      });
    }, 2200);
  });
}

/* ---------- mock Google Places (address autocomplete) ---------- */
export const MOCK_ADDRESSES = [
  "12 Marine Parade, Bondi Beach NSW 2026",
  "44 Smith Street, Collingwood VIC 3066",
  "8/210 Coronation Drive, Milton QLD 4064",
  "67 Hindley Street, Adelaide SA 5000",
  "3 Rokeby Road, Subiaco WA 6008",
  "19 Murray Street, Hobart TAS 7000",
  "120 Bourke Street, Melbourne VIC 3000",
  "5 Macquarie Place, Sydney NSW 2000",
];
export function lookupAddress(q) {
  const s = (q || "").trim().toLowerCase();
  if (s.length < 2) return [];
  const hits = MOCK_ADDRESSES.filter((a) => a.toLowerCase().includes(s));
  const base = hits.length ? hits : MOCK_ADDRESSES;
  return base.slice(0, 4).map((a) => (s.length >= 3 && !hits.length ? a.replace(/^\d+/, s.replace(/\D/g, "") || "1") : a));
}

/* ---------- option sets ---------- */
export const OPT = {
  residentialStatus: [
    { v: "OWN_MORTGAGE", l: "Own with mortgage" },
    { v: "OWN_NO_MORTGAGE", l: "Own outright" },
    { v: "RENTING", l: "Renting" },
    { v: "BOARDING", l: "Boarding" },
    { v: "WITH_PARENTS", l: "Living with parents" },
    { v: "CARAVAN", l: "Caravan / mobile" },
    { v: "OTHER", l: "Other" },
  ],
  maritalStatus: [
    { v: "MARRIED", l: "Married", icon: "ring" },
    { v: "DEFACTO", l: "De facto", icon: "heart" },
    { v: "SINGLE", l: "Single", icon: "user" },
    { v: "DIVORCED", l: "Divorced", icon: "users" },
    { v: "WIDOWED", l: "Widowed", icon: "users" },
  ],
  residencyStatus: [
    { v: "AU_CITIZEN", l: "Australian citizen" },
    { v: "AU_PERMANENT_RESIDENT", l: "Permanent resident" },
    { v: "AU_TAX_RESIDENT", l: "Resident for tax purposes" },
    { v: "NON_AU_RESIDENT", l: "Non-resident" },
  ],
  employmentType: [
    { v: "PAYG", l: "Full / part-time (PAYG)" },
    { v: "CASUAL", l: "Casual" },
    { v: "CONTRACT", l: "Contract" },
    { v: "SELF_EMPLOYED", l: "Self-employed" },
    { v: "RETIRED", l: "Retired" },
    { v: "UNEMPLOYED", l: "Not currently working" },
  ],
  employmentTime: [
    { v: "FULL_TIME", l: "Full time" },
    { v: "PART_TIME", l: "Part time" },
    { v: "CASUAL", l: "Casual" },
  ],
  incomePeriod: [
    { v: "WEEKLY", l: "Weekly" },
    { v: "FORTNIGHTLY", l: "Fortnightly" },
    { v: "MONTHLY", l: "Monthly" },
    { v: "ANNUALLY", l: "Annually" },
  ],
  assetType: [
    { v: "PROPERTY", l: "Property", icon: "home" },
    { v: "VEHICLE", l: "Vehicle", icon: "car" },
    { v: "SHARES", l: "Shares / investments", icon: "trend" },
    { v: "SUPERANNUATION", l: "Superannuation", icon: "wallet" },
    { v: "SAVINGS", l: "Savings", icon: "bank" },
    { v: "OTHER", l: "Other asset", icon: "spark" },
  ],
  liabilityType: [
    { v: "HOME_LOAN", l: "Home loan", icon: "home" },
    { v: "CAR_LOAN", l: "Car loan", icon: "car" },
    { v: "PERSONAL_LOAN", l: "Personal loan", icon: "wallet" },
    { v: "CREDIT_CARD", l: "Credit card", icon: "cart" },
    { v: "BNPL", l: "Buy now pay later", icon: "cart" },
    { v: "SALARY_ADVANCE", l: "Salary advance", icon: "briefcase" },
    { v: "OTHER", l: "Other debt", icon: "doc" },
  ],
  // Asset-finance entity types. Mirrored in admin/fields.jsx FIELD_META.
  entityType: [
    { v: "COMPANY", l: "Company", icon: "briefcase" },
    { v: "SOLE_TRADER", l: "Sole trader", icon: "user" },
    { v: "PARTNERSHIP", l: "Partnership", icon: "users" },
    { v: "TRUST", l: "Trust", icon: "doc" },
  ],
  /* ---- Your business situation (asset finance) ----
     Every one of these is mirrored in admin/fields.jsx FIELD_META so ops can
     correct a captured answer with exactly the value the form would store. */
  // Tick all that apply — a business that takes cards AND invoices says so by
  // choosing both, which is why there's no "Mixed" option to guess at.
  paymentMethods: [
    { v: "EFTPOS", l: "EFTPOS / card in person", icon: "cart" },
    { v: "ONLINE_STORE", l: "Online store", icon: "cart" },
    { v: "INVOICES", l: "Invoices", icon: "doc" },
    { v: "MARKETPLACE", l: "Marketplace (Shopify, Amazon, eBay)", icon: "cart" },
    { v: "CASH", l: "Cash", icon: "wallet" },
  ],
  businessPremises: [
    { v: "HOME", l: "Home", icon: "home" },
    { v: "COMMERCIAL", l: "Commercial premises", icon: "briefcase" },
    { v: "MOBILE", l: "Mobile", icon: "car" },
    { v: "ONLINE_ONLY", l: "Online only", icon: "cart" },
    { v: "OTHER", l: "Other", icon: "spark" },
  ],
  employeeCount: [
    { v: "NONE", l: "None" },
    { v: "1_5", l: "1–5" },
    { v: "6_PLUS", l: "6 or more" },
  ],
  // What the owners actually take out. "Whatever's left over" is the honest
  // answer for a great many small businesses and has to be pickable, or the
  // owner leaves the question blank and we can't tell that from unanswered.
  ownerDrawingsBasis: [
    { v: "AMOUNT", l: "A set amount each month", icon: "wallet" },
    { v: "LEFTOVER", l: "No set wage - the owners take what's left over", icon: "trend" },
  ],
  // How the current year is tracking against the last full one — the single
  // most useful thing an owner can tell us that a tax return can't.
  tradingTrend: [
    { v: "UP", l: "Better than last year", icon: "trend" },
    { v: "SAME", l: "About the same", icon: "scale" },
    { v: "DOWN", l: "Down on last year", icon: "info" },
  ],
  /* ---- Related parties (asset finance) ---- */
  // Who this person is to the entity. A guarantor alone isn't enough to
  // lodge — the form insists on at least one principal.
  partyRelationship: [
    { v: "DIRECTOR", l: "Director", icon: "briefcase" },
    { v: "SOLE_TRADER", l: "Sole trader", icon: "user" },
    { v: "PARTNER", l: "Partner", icon: "users" },
    { v: "TRUSTEE", l: "Trustee", icon: "doc" },
    { v: "GUARANTOR", l: "Guarantor", icon: "shield" },
  ],
  title: [
    { v: "MR", l: "Mr" }, { v: "MRS", l: "Mrs" }, { v: "MS", l: "Ms" },
    { v: "MISS", l: "Miss" }, { v: "DR", l: "Dr" }, { v: "OTHER", l: "Other" },
  ],
  gender: [
    { v: "MALE", l: "Male" }, { v: "FEMALE", l: "Female" },
    { v: "OTHER", l: "Other" }, { v: "UNDISCLOSED", l: "Prefer not to say" },
  ],
  taxPosition: [
    { v: "NO_ATO_DEBT", l: "No ATO debt", icon: "check" },
    { v: "PAYMENT_ARRANGEMENT", l: "Payment arrangement in place", icon: "calendar" },
    { v: "OUTSTANDING_DEBT", l: "Outstanding debt", icon: "scale" },
    { v: "UNSURE", l: "Not sure", icon: "info" },
  ],
  loanPurpose: [
    { v: "DEBT_CONSOLIDATION", l: "Debt consolidation", icon: "scale" },
    { v: "HOME_IMPROVEMENT", l: "Home improvement", icon: "home" },
    { v: "TRAVEL", l: "Travel & holiday", icon: "spark" },
    { v: "WEDDING", l: "Wedding or event", icon: "ring" },
    { v: "MEDICAL", l: "Medical or dental", icon: "heart" },
    { v: "MAJOR_PURCHASE", l: "Major purchase", icon: "cart" },
    { v: "EDUCATION", l: "Education", icon: "doc" },
    { v: "OTHER", l: "Something else", icon: "wallet" },
  ],
};

/* ---------- expense items — phrased for the individual applicant (their share) ----------
   `max` = slider ceiling (defaults to 3000 in the widget); typing isn't
   affected server-side — these are UI ranges only. */
export const EXPENSE_GROUPS = [
  { id: "home", label: "Home & bills", items: [
    { k: "rent", l: "Your share of rent / mortgage" },
    { k: "utilities", l: "Your share of utilities" },
    { k: "telecommunications", l: "Telecommunications", max: 1500 },
    { k: "insurance", l: "Insurance", max: 1500 },
  ] },
  { id: "everyday", label: "Everyday living", items: [
    { k: "groceries", l: "Your share of groceries" },
    { k: "diningOut", l: "Dining out" },
    { k: "vehiclesTransport", l: "Vehicles and transport", max: 1500 },
  ] },
  { id: "lifestyle", label: "Lifestyle & family", items: [
    { k: "subscriptionTv", l: "Subscription TV", max: 1000 },
    { k: "entertainment", l: "Entertainment", max: 1500 },
    { k: "educationChildcare", l: "Education & childcare" },
  ] },
];

/* ---------- initial form state ---------- */
export const INITIAL = {
  // step 1
  firstName: "", lastName: "", dateOfBirth: "", phone: "", email: "", address: "",
  contactOptIn: false,
  // step 2
  vehicleFound: null, rego: "", vehicle: null, isNewVehicle: null, bodyType: null,
  loanPurpose: "",
  // Personal loans, purpose "Something else": what the funds are actually
  // for, in the applicant's words - required whenever OTHER is picked, so
  // credit assesses the true purpose instead of a catch-all.
  loanPurposeOther: "",
  // Personal loans only: the vehicle the loan is secured against. The form
  // never captures a vehicle in the personal flow otherwise (no lookup, no
  // VIN - those questions are the car flow's). The rego feeds the secured
  // lender envelope (replacing its TBC placeholder - see the lender mapper); the
  // odometer stays internal for the assessment team.
  securityRego: "", securityOdometer: "",
  loanAmount: 25000, depositAmount: 0,
  // step 3
  isExistingCustomer: null, yearsAtAddress: 3, previousAddress: "",
  postalSame: true, postalAddress: "", residentialStatus: "", numberOfDependents: 0,
  maritalStatus: "", residencyStatus: "",
  // step 4 · identity (single document of choice)
  idDocType: "", idDocNumber: "", idDocOtherType: "",
  // step 5 · needs analysis
  loanTerm: 5, paymentFrequency: "monthly", repaymentAwareness: null, repaymentIssueDetails: "",
  // step 6 · bank ("illionConfirmedBy": how completion was established —
  // "signal" (postMessage/bridge) or "manual" (applicant confirmed) — so ops
  // can see which files lack a signal-confirmed verification)
  illionReference: "", illionConfirmedBy: "",
  // lender application created at Step 6 (persisted so a refresh reuses the same
  // application instead of creating a duplicate)
  _applicationId: "", lenderReference: "", bankStatementsUrl: "",
  // server-side draft resume token (returned once when the draft is created)
  _resumeToken: "",
  // estimate basis (no product step — indicative pricing)
  selectedProduct: "secured",
  // step 7
  employmentType: "", employmentTime: "", employerCompanyName: "", employerContactNumber: "", occupationName: "",
  grossSalary: "", incomePeriod: "ANNUALLY", commencementDate: "", isUnderProbation: null,
  expenses: {},
  // step 8
  assets: [], liabilities: [], noAssets: false, noLiabilities: false,
  // step 9 — consentPrivacyPolicy and consentDataExchange are ticked by ONE
  // checkbox (the label links both documents); consentCreditGuide is its own.
  consentCreditCheck: false, consentPrivacyPolicy: false, consentDataExchange: false,
  consentCreditGuide: false, consentComms: false,
};

/* ---------- step meta ----------
   Every step the platform can show, keyed by id. A product picks a SEQUENCE
   from these (below) — asset finance asks different questions in a different
   order, so a product supplies its whole list rather than swapping one slot.
   Step numbers are derived from position, never hardcoded, so inserting a
   step is a one-line change to a sequence. */
const STEP_LIBRARY = {
  lead:      { id: "lead",      title: "Let's get started",    kicker: "Your details",       icon: "user" },
  vehicle:   { id: "vehicle",   title: "About the car",        kicker: "Vehicle & amount",   icon: "car" },
  purpose:   { id: "purpose",   title: "About your loan",      kicker: "Purpose & amount",   icon: "wallet" },
  entity:    { id: "entity",    title: "About the business",   kicker: "Business & amount",  icon: "briefcase" },
  situation: { id: "situation", title: "Your business situation", kicker: "How the business runs", icon: "trend" },
  parties:   { id: "parties",   title: "Who's behind the business", kicker: "Directors & guarantors", icon: "users" },
  financials:{ id: "financials", title: "How the business is trading", kicker: "Business financials", icon: "trend" },
  customer:  { id: "customer",  title: "A bit about you",      kicker: "Your situation",     icon: "home" },
  identity:  { id: "identity",  title: "Your ID documents",    kicker: "ID document",        icon: "id" },
  needs:     { id: "needs",     title: "Your loan term",       kicker: "Needs analysis",     icon: "calendar" },
  bank:      { id: "bank",      title: "Verify your income",   kicker: "Bank statements",    icon: "bank" },
  income:    { id: "income",    title: "Income & expenses",    kicker: "Affordability",      icon: "briefcase" },
  assets:    { id: "assets",    title: "Assets & liabilities", kicker: "Your balance sheet", icon: "scale" },
  review:    { id: "review",    title: "Review & submit",      kicker: "Confirmation",       icon: "check" },
};

/* Which steps each product asks, in order. Car and personal are the
   historical nine (they differ only at slot 2) — pinned by test so this
   refactor can't change what an existing applicant sees. The asset-finance
   sequence grows as its steps are built: business situation, the people
   behind the entity, and business financials. */
export const STEP_SEQUENCES = {
  car:        ["lead", "vehicle", "customer", "identity", "needs", "bank", "income", "assets", "review"],
  personal:   ["lead", "purpose", "customer", "identity", "needs", "bank", "income", "assets", "review"],
  // "customer" (the single-applicant personal-situation step) is replaced by
  // "parties": an asset-finance application is about the people behind an
  // entity, not one borrower.
  commercial: ["lead", "entity", "situation", "parties", "identity", "needs", "bank", "financials", "assets", "review"],
};

/* The step list a product asks for, gate aside. getSteps() goes through
   getProduct(), which falls back to car while asset finance is switched off —
   this is the raw sequence, so the flow can be built and tested before the
   product is switched on anywhere. */
export const stepsForSequence = (product) => buildSteps(STEP_SEQUENCES[product] || STEP_SEQUENCES.car);

const buildSteps = (ids) => ids.map((id, i) => ({ ...STEP_LIBRARY[id], num: i + 1, color: "#2864F0" }));

export const BASE_STEPS = buildSteps(STEP_SEQUENCES.car);

/* Balloon (residual) percentages offered on asset finance. 30% is the ceiling
   the asset-finance book writes, so the list IS the cap - there is no free
   entry to exceed it. Mirrored in server/lib/canonical.js (which clamps to
   the same set) and admin/fields.jsx. */
export const BALLOON_OPTIONS = [0, 10, 20, 30];
export const BALLOON_MAX = 30;

export const LOAN_PRODUCTS = {
  car: {
    id: "car", label: "Car loan", docTitle: `${BRAND.name} - Car Loan Application`,
    amountMin: 8000, amountMax: 50000,
  },
  personal: {
    id: "personal", label: "Personal loan", docTitle: `${BRAND.name} - Personal Loan Application`,
    // The lender product's pricing range: every row on the lender's personal
    // product prices $8,000-$60,000 (their pricing table, 2026-08-07), so an
    // amount outside it can never be funded. MIRROR of the amountRange rule
    // in server/lib/profiles.js - change both together (asserted in
    // scripts/api-test.mjs).
    amountMin: 8000, amountMax: 60000,
  },
  // Asset finance — the entity step is a stand-in until the business
  // questions land; its sequence above is where new steps slot in.
  commercial: {
    id: "commercial", label: "Asset finance", docTitle: `${BRAND.name} - Asset Finance Application`,
    amountMin: 10000, amountMax: 500000,
  },
};
/* Asset finance is still being built, so the product is off unless the build
   opts in with VITE_COMMERCIAL_PRODUCT=on (production leaves it unset;
   staging turns it on to preview). The build-time twin of the server's
   COMMERCIAL_PRODUCT gate in server/lib/profiles.js — both must be on, or
   ?product=commercial quietly falls back to a car loan. Guarded because
   import.meta.env only exists under Vite. */
const COMMERCIAL_ON = (() => {
  try { return String(import.meta.env?.VITE_COMMERCIAL_PRODUCT ?? "").trim().toLowerCase() === "on"; }
  catch { return false; }
})();

export function getProduct(product) {
  const cfg = LOAN_PRODUCTS[product];
  if (!cfg || (cfg.id === "commercial" && !COMMERCIAL_ON)) return LOAN_PRODUCTS.car;
  return cfg;
}
export function getSteps(product) {
  const cfg = getProduct(product);
  return buildSteps(STEP_SEQUENCES[cfg.id] || STEP_SEQUENCES.car);
}
export const STEPS = getSteps("car");

/* live estimate helper — current best repayment for the hero */
export function liveEstimate(f) {
  const principal = Math.max(0, (Number(f.loanAmount) || 0) - (Number(f.depositAmount) || 0));
  const prod = PRODUCTS.find((p) => p.id === f.selectedProduct);
  const rate = prod ? prod.rate : INDICATIVE_RATE;
  const term = Number(f.loanTerm) || 5;
  const freq = f.paymentFrequency || "monthly";
  return { principal, rate, term, freq, amount: calcPayment(principal, term, rate, freq) };
}
